import type { ReactNode } from "react";
import { MenuSelect, type MenuSelectOption } from "@/components/MenuSelect";
import { ProviderIcon } from "@/components/ProviderIcon";
import { sentenceCase } from "@/lib/sentenceCase";
import type { ProviderStatus, ReasoningEffort } from "@rivets/core";

/**
 * The runtime chrome shared by the chat composer and the new-task composer.
 *
 * Both surfaces offer the same choices, so they render the same controls at the
 * same fixed heights. Every menu is width-pinned and truncates, so changing a
 * selection never reflows the footer row it lives in.
 *
 * Two anatomies, and only two. A *menu* is an optional 12px provider logo, the
 * chosen word, and an 8px chevron sitting snug against it — no other icon and
 * no caption, because the word already names the choice and a gauge or a server
 * rack beside it just added a second thing to read. A *toggle* is a glyph-only
 * 24px square, so the two kinds never get confused for each other. The footer
 * separates them with one hairline rather than by hoping the reader notices.
 *
 * Icons are inline SVGs at one size and one stroke weight — the text glyphs they
 * replaced (`ϟ`, `◇`, `▥`, `>_`) rendered at whatever weight the user's emoji
 * font decided on, which is what made the row look assembled from spare parts.
 */

/** Every runtime icon shares this box and stroke so they read as one set. */
function RuntimeIcon({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <svg
      className="runtime-icon"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

/** A checklist: two ticked lines, for plan mode. */
function PlanIcon() {
  return (
    <RuntimeIcon>
      <path d="M1.3 3.6 2.4 4.7 4.4 2.5" />
      <path d="M1.3 8.6 2.4 9.7 4.4 7.5" />
      <path d="M6.4 3.7h4.3M6.4 8.8h4.3" />
    </RuntimeIcon>
  );
}

/** A bolt, filled so it holds its weight at 12px. */
function FastIcon() {
  return (
    <svg className="runtime-icon" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M6.9 1 3 6.6h2.4L5.1 11 9 5.4H6.6L6.9 1Z" fill="currentColor" />
    </svg>
  );
}

/** Encodes a provider+model pair as one menu value. */
export function agentModelValue(providerId: string, modelId: string | undefined): string {
  return JSON.stringify([providerId, modelId ?? ""]);
}

export function parseAgentModelValue(value: string): [string, string] {
  return JSON.parse(value) as [string, string];
}

export function agentModelOptions(statuses: readonly ProviderStatus[]): MenuSelectOption[] {
  return statuses.flatMap((status) => status.models.map((model) => ({
    value: agentModelValue(status.providerId, model.id),
    label: model.displayName,
    group: status.displayName,
    // Typing a raw model id ("claude-opus-5") is how people who know the
    // catalogue search it, so the id is matched even though it is not shown.
    keywords: `${model.displayName} ${model.id} ${status.displayName} ${status.providerId}`,
    icon: <ProviderIcon providerId={status.providerId} />,
  })));
}

export function AgentModelPicker({
  providerId,
  modelId,
  statuses,
  disabled,
  title,
  onChange,
}: {
  providerId: string;
  modelId: string | undefined;
  statuses: readonly ProviderStatus[];
  disabled?: boolean;
  title?: string;
  onChange: (value: string) => void;
}) {
  const options = agentModelOptions(statuses);
  const status = statuses.find((candidate) => candidate.providerId === providerId);
  // There is no "default" row: an unset model shows as the provider's default
  // model when it names one, and as the provider otherwise.
  const resolvedModelId = status?.models.some((model) => model.id === modelId)
    ? modelId
    : status?.models.find((model) => model.isDefault)?.id;
  const value = agentModelValue(providerId, resolvedModelId);
  return (
    <MenuSelect
      className="runtime-agent-picker"
      ariaLabel="Agent and model"
      title={title}
      // The provider's logo, as on the chat tabs — a logo reads as identity,
      // where a coloured dot here was mistaken for run status.
      leading={providerId ? <ProviderIcon providerId={providerId} /> : undefined}
      value={value}
      triggerLabel={resolvedModelId || !status ? undefined : status.displayName}
      options={options}
      onChange={onChange}
      disabled={disabled || statuses.length === 0}
      placeholder={statuses.length === 0 ? "No agent" : "Choose agent"}
      // Always searchable: this is the one menu that spans every provider, so
      // typing has to be the way through it however short today's list is.
      searchable
      searchPlaceholder="Search agents and models…"
      // Still pinned, so switching model cannot reflow the row — but narrower
      // now that the "Agent" caption is gone, and the slack sits after the
      // chevron rather than between the label and it.
      minWidth={148}
      maxWidth={148}
      size="sm"
    />
  );
}

/** How many of the five bars are lit for level `index` of `count` efforts.
 *
 * Providers declare between two and five rungs, and the meter is always five
 * bars — so the ladder is mapped evenly onto it rather than leaving the top of
 * the chart permanently dark on a provider that only offers three levels. */
export function litEffortBars(index: number, count: number): number {
  if (count <= 0 || index < 0) return 0;
  return Math.min(EFFORT_BAR_COUNT, Math.max(1, Math.round(((index + 1) / count) * EFFORT_BAR_COUNT)));
}

const EFFORT_BAR_COUNT = 5;

/** The five-bar meter left of the word: bottom-aligned, 2px wide, 3–11px tall.
 * Drawn with spans rather than an SVG so the two tones are one class swap and
 * the heights stay in the sheet with the rest of the control's geometry. */
function EffortBars({ lit }: { lit: number }) {
  return (
    <span className="effort-bars" aria-hidden="true">
      {Array.from({ length: EFFORT_BAR_COUNT }, (_, index) => (
        <span key={index} className={`effort-bar${index < lit ? " lit" : ""}`} />
      ))}
    </span>
  );
}

/**
 * Effort is a short ordered ladder, not a catalogue. A menu made choosing the
 * next rung a three-step gesture — open, read four words, aim — to move one
 * notch along a scale the reader already understands, so it cycles on click
 * instead, wrapping in the provider's declared order.
 *
 * There is deliberately no chevron: a chevron promises a list, and this control
 * has none to show. The word alone, in the same 24px pinned-width box the menus
 * use, so the footer stays one row of same-sized things.
 *
 * `unavailable` keeps the slot and dims it rather than unmounting: the footer
 * has to stay the same shape whichever model is selected.
 */
export function EffortPicker({
  value,
  efforts,
  disabled,
  unavailable = false,
  onChange,
}: {
  value: ReasoningEffort | undefined;
  efforts: readonly ReasoningEffort[];
  disabled?: boolean;
  unavailable?: boolean;
  onChange: (value: ReasoningEffort) => void;
}) {
  // Gating unchanged: a provider with no efforts to offer has no control here.
  if (efforts.length === 0 && !unavailable) return null;
  const current = value && efforts.includes(value) ? value : efforts[0];
  const cycle = () => {
    if (efforts.length === 0) return;
    // `indexOf` of an unlisted value is -1, so an effort the provider dropped
    // lands on the first rung rather than on nothing.
    const next = efforts[(efforts.indexOf(current as ReasoningEffort) + 1) % efforts.length];
    if (next) onChange(next);
  };
  const lit = unavailable || !current ? 0 : litEffortBars(efforts.indexOf(current), efforts.length);
  return (
    <button
      type="button"
      className={`runtime-effort-button${unavailable ? " is-unavailable" : ""}`}
      aria-label="Reasoning effort"
      title={unavailable ? "This model has no reasoning-effort control" : "Reasoning effort — click to cycle"}
      disabled={disabled || unavailable || efforts.length < 2}
      onClick={cycle}
    >
      <EffortBars lit={lit} />
      <span className="runtime-effort-word">{unavailable || !current ? "Effort" : sentenceCase(current)}</span>
    </button>
  );
}

export function RuntimeToggle({
  kind,
  active,
  disabled,
  unavailable = false,
  onToggle,
}: {
  kind: "fast" | "plan";
  active: boolean;
  disabled?: boolean;
  /** The selected model does not have this capability. The switch stays in the
   * row, dimmed and inert, so changing model never re-flows the footer. */
  unavailable?: boolean;
  onToggle: () => void;
}) {
  const label = kind === "fast" ? "fast mode" : "plan mode";
  return (
    <button
      type="button"
      className={`composer-toggle${kind === "plan" ? " composer-plan-toggle" : ""}${active ? " active" : ""}${unavailable ? " is-unavailable" : ""}`}
      aria-label={`${active ? "Disable" : "Enable"} ${label}`}
      aria-pressed={active}
      title={unavailable ? `${sentenceCase(label)} is not available for this model` : sentenceCase(label)}
      disabled={disabled || unavailable}
      onClick={onToggle}
    >{kind === "fast" ? <FastIcon /> : <PlanIcon />}</button>
  );
}
